(() => {
  const busy = new WeakSet();

  const statusOf = (root) => root.querySelector('[data-card-quick-status]');

  const setStatus = (root, text) => {
    const status = statusOf(root);
    if (status) status.textContent = text;
  };

  const modals = () => (window.Alpine && window.Alpine.store ? window.Alpine.store('modals') : null);

  const openDrawerEarly = (root) => {
    if (!root.hasAttribute('data-open-drawer')) return false;
    const store = modals();
    if (!store || !store.modals || !store.modals.cart) return false;
    document.dispatchEvent(new CustomEvent('baseline:cart:lock'));
    store.open('cart');
    return true;
  };

  const sizePanel = (root, panel) => {
    const meta = root.closest('.ka-card-meta') || root.parentElement;
    const metaRect = meta.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const inset = parseFloat(getComputedStyle(meta).paddingLeft) || 8;
    panel.style.setProperty('--ka-panel-width', `${Math.max(rootRect.left - metaRect.left, 0)}px`);
    panel.style.setProperty('--ka-panel-top', `${metaRect.top - rootRect.top}px`);
    panel.style.setProperty('--ka-panel-height', `${metaRect.height}px`);
    panel.style.setProperty('--ka-panel-inset', `${inset}px`);
    panel.style.setProperty('--choice-count', panel.children.length);
    panel.classList.remove('is-stacked');
    const needed = [...panel.children].reduce((total, choice) => total + choice.getBoundingClientRect().width, inset);
    if (needed > panel.getBoundingClientRect().width + 1) panel.classList.add('is-stacked');
  };

  const closePanel = (root) => {
    const panel = root.querySelector('[data-card-quick-panel]');
    const trigger = root.querySelector('[data-card-quick-trigger]');
    if (!panel) return;
    root.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  const openPanel = (root) => {
    document.querySelectorAll('[data-card-quick-add].is-open').forEach((open) => {
      if (open !== root) closePanel(open);
    });
    const panel = root.querySelector('[data-card-quick-panel]');
    const trigger = root.querySelector('[data-card-quick-trigger]');
    if (!panel) return;
    sizePanel(root, panel);
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');
    root.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const last = panel.querySelector('[data-card-quick-variant]:last-child');
    if (last && trigger && trigger.matches(':focus-visible')) {
      window.setTimeout(() => {
        if (root.classList.contains('is-open')) last.focus();
      }, 120);
    }
  };

  const addVariant = async (root, variantId) => {
    if (busy.has(root)) return;
    busy.add(root);
    root.classList.add('is-busy');
    root.setAttribute('aria-busy', 'true');
    setStatus(root, 'Adding to cart');

    const openedEarly = openDrawerEarly(root);
    closePanel(root);

    try {
      const body = new FormData();
      body.append('id', String(variantId));
      body.append('quantity', '1');
      body.append('sections', 'cart-items,cart-footer,cart-item-count');
      body.append('sections_url', window.location.pathname);

      const response = await fetch(window.theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        if (openedEarly) modals().close('cart');
        setStatus(root, data.description || data.message || 'Could not add to cart');
        return;
      }

      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:afteradditem', {
          bubbles: true,
          detail: {
            response: data,
            sourceId: 'card-quick-add',
          },
        })
      );
      setStatus(root, 'Added to cart');
    } catch (error) {
      if (openedEarly) modals().close('cart');
      setStatus(root, 'Could not add to cart');
    } finally {
      if (openedEarly) document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
      busy.delete(root);
      root.classList.remove('is-busy');
      root.removeAttribute('aria-busy');
    }
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target.closest('.ka-card-quick__plus:not(:disabled), [data-card-quick-variant]');
      if (!target) return;
      const root = target.closest('[data-card-quick-add]');
      if (!root) return;
      root.classList.add('is-pressed');
      const release = () => {
        root.classList.remove('is-pressed');
        window.removeEventListener('pointerup', release);
        window.removeEventListener('pointercancel', release);
      };
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    },
    { passive: true }
  );

  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-card-quick-variant]');
    if (choice) {
      event.preventDefault();
      event.stopPropagation();
      const root = choice.closest('[data-card-quick-add]');
      if (root) addVariant(root, choice.dataset.cardQuickVariant);
      return;
    }

    const trigger = event.target.closest('[data-card-quick-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const root = trigger.closest('[data-card-quick-add]');
      if (!root || busy.has(root)) return;
      if (root.dataset.mode === 'select') {
        if (root.classList.contains('is-open')) {
          closePanel(root);
        } else {
          openPanel(root);
        }
        return;
      }
      if (root.dataset.variantId) addVariant(root, root.dataset.variantId);
      return;
    }

    if (!event.target.closest('[data-card-quick-add]')) {
      document.querySelectorAll('[data-card-quick-add].is-open').forEach(closePanel);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-card-quick-add].is-open').forEach((root) => {
      closePanel(root);
      const trigger = root.querySelector('[data-card-quick-trigger]');
      if (trigger) trigger.focus();
    });
  });

  let lastWidth = window.innerWidth;
  window.addEventListener(
    'resize',
    () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      document.querySelectorAll('[data-card-quick-add].is-open').forEach(closePanel);
    },
    { passive: true }
  );
})();
